import React, { useState, useEffect, useRef } from 'react';
import {
  Package,
  CheckCircle,
  Truck,
  AlertCircle,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { dashboardService, DashboardMetrics } from '../services/dashboard.service';
import TrackingExecutionList from '../components/TrackingExecutionList';

export default function DashboardPage() {
  const tableRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [trackingMetrics, setTrackingMetrics] = useState<any>(null);
  const [appliedFilter, setAppliedFilter] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setError(null);
      const [metricsData, trackingData] = await Promise.all([
        dashboardService.getMetrics(),
        dashboardService.getTrackingMetrics()
      ]);

      setMetrics(metricsData);
      setTrackingMetrics(trackingData);
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  const handleStatClick = (filter: string) => {
    setAppliedFilter(filter);
    setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const stats = trackingMetrics ? [
    {
      title: 'Entregues',
      value: formatNumber(trackingMetrics.delivered || 0),
      icon: CheckCircle,
      color: 'from-green-500 to-green-600',
      filter: 'delivered'
    },
    {
      title: 'Total Ativos',
      value: formatNumber(trackingMetrics.totalActive || 0),
      icon: Package,
      color: 'from-blue-500 to-blue-600',
      filter: 'active'
    },
    {
      title: 'Em Atividade',
      value: formatNumber(trackingMetrics.inActivity || 0),
      icon: Truck,
      color: 'from-purple-500 to-purple-600',
      filter: 'inActivity'
    },
    {
      title: 'Erros Ativos',
      value: formatNumber(trackingMetrics.activeErrors || 0),
      icon: AlertCircle,
      color: 'from-red-500 to-red-600',
      filter: 'activeErrors'
    },
  ] : [];


  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <p className="mt-4 text-gray-600">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">
          Visão geral do sistema de rastreamento
          {metrics?.lastUpdate && ` • Atualizado: ${new Date(metrics.lastUpdate).toLocaleString('pt-BR')}`}
        </p>
      </div>

      {/* Stats Grid - Dados Indexados */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Rastreamentos Indexados</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div 
                  className="relative overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => 'filter' in stat && handleStatClick(stat.filter)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">{stat.title}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="absolute -right-6 -bottom-6 w-24 h-24 opacity-10">
                    <Icon className="w-full h-full" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Tracking Execution List */}
      <motion.div
        ref={tableRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-6"
      >
        <TrackingExecutionList externalFilter={appliedFilter} onFilterApplied={() => setAppliedFilter(null)} />
      </motion.div>

    </div>
  );
}